import { useEffect, useRef, useState } from "react";
import { Check, Clock, Hourglass, Lock, Play, RotateCcw, Sparkles } from "lucide-react";
import { Badge, Button, Card, cx, Textarea } from "@/components/ui";
import { Points } from "@/components/bits";
import { Sheet } from "@/components/Sheet";
import { api } from "@/lib/api";
import { useAction, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { REPEAT_LABELS } from "@/lib/format";
import { formatUnits, isConvertible, unitsForPoints } from "@shared/economy";
import { screenLevelFor } from "@shared/screenLevel";
import type { GateDTO, KidJobDTO, RewardTypeDTO } from "@shared/types";

/** Which kid screens to show the signed-in kid. */
export function useScreenLevel() {
  const { user } = useSession();
  return screenLevelFor(user.age, user.screenLevel);
}

/** Counts each time finishing the list unlocks jobs. Use it as the key of a <Confetti />. */
export function useUnlockCelebration(gate: GateDTO | undefined, onUnlock?: () => void) {
  const [celebrate, setCelebrate] = useState(0);
  const wasLocked = useRef<boolean | null>(null);
  useEffect(() => {
    if (!gate) return;
    if (wasLocked.current === true && gate.unlocked && gate.total > 0) {
      setCelebrate((n) => n + 1);
      onUnlock?.();
    }
    wasLocked.current = !gate.unlocked;
  }, [gate]);
  return celebrate;
}

/** What a job pays for the time it takes, or null when no time estimate was given. */
export function hourlyRate(job: Pick<KidJobDTO, "reward" | "estimatedMinutes">): number | null {
  return job.estimatedMinutes ? Math.round((job.reward / job.estimatedMinutes) * 60) : null;
}

/** Express points in the reward that matters most to this kid ("= 45 min Minecraft"). */
export function useMeaningful() {
  const { user } = useSession();
  const { data } = useRewardTypes();
  const types = (data?.rewardTypes ?? []).filter((t) => t.active && isConvertible(t));
  const favorite: RewardTypeDTO | undefined = types.find((t) => t.id === user.favoriteRewardTypeId) ?? types[0];
  return {
    favorite,
    describe(points: number): string | null {
      if (!favorite?.pointsPerUnit || points <= 0) return null;
      const units = unitsForPoints(points, favorite.pointsPerUnit);
      if (units <= 0) return null;
      return favorite.kind === "cash" ? formatUnits(units, favorite) : `${formatUnits(units, favorite)} of ${favorite.name}`;
    },
  };
}

export function JobCard({ job, compact }: { job: KidJobDTO; compact?: boolean }) {
  const { family } = useSession();
  const meaningful = useMeaningful();
  const older = useScreenLevel() === "older";
  const rate = older ? hourlyRate(job) : null;
  const [doneSheet, setDoneSheet] = useState<"claim" | "submit" | null>(null);
  const [note, setNote] = useState("");
  const claim = useAction((submit: boolean) => api.post(`/jobs/${job.id}/claim`, { submit, note: note || undefined }), {
    success: (r) => ((r as { status: string }).status === "submitted" ? "Sent for approval! 🎉" : "Job started — go get it!"),
    onSuccess: () => {
      setDoneSheet(null);
      setNote("");
    },
  });
  const submit = useAction(() => api.post(`/job-runs/${job.activeRun!.id}/submit`, { note: note || undefined }), {
    success: "Sent for approval! 🎉",
    onSuccess: () => {
      setDoneSheet(null);
      setNote("");
    },
  });
  const cancel = useAction(() => api.post(`/job-runs/${job.activeRun!.id}/cancel`), { success: "Job dropped" });

  const equivalent = meaningful.describe(job.reward);
  const run = job.activeRun;

  return (
    <Card className={cx("relative overflow-hidden p-3", job.locked && !run && "bg-sunken/60")}>
      <div className="flex items-center gap-3">
        <span
          className={cx(
            "relative flex size-14 shrink-0 items-center justify-center rounded-2xl text-3xl",
            job.locked && !run ? "bg-locked-soft grayscale-[60%]" : job.workItOff ? "bg-violet-soft" : "bg-coin-soft",
          )}
        >
          {job.icon}
          {job.locked && !run && (
            <span className="absolute -bottom-1 -right-1 flex size-6 items-center justify-center rounded-full bg-locked text-white ring-2 ring-card">
              <Lock className="size-3.5" />
            </span>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[17px] font-extrabold">{job.title}</div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            <Points value={job.reward} family={family} className={job.workItOff ? "text-violet" : "text-coin-ink"} />
            {equivalent && !job.workItOff && <span className="text-muted">≈ {equivalent}</span>}
            {job.workItOff && <span className="text-violet">toward getting back to zero</span>}
            {rate !== null && (
              <span className="text-muted">
                {job.estimatedMinutes} min · {rate.toLocaleString()}/hr
              </span>
            )}
          </div>
          {!compact && (
            <div className="mt-1 flex flex-wrap gap-1">
              <Badge>{REPEAT_LABELS[job.repeat]}</Badge>
              {job.origin === "pitch" && (
                <Badge tone="violet">
                  <Sparkles className="size-3" /> Your idea
                </Badge>
              )}
              {job.workItOff && <Badge tone="violet">Work it off</Badge>}
            </div>
          )}
        </div>
      </div>

      {!compact && job.description && <p className="mt-2 text-sm text-muted">{job.description}</p>}

      <div className="mt-3 flex gap-2">
        {run?.status === "submitted" ? (
          <Badge tone="warn" className="px-3 py-1.5 text-sm">
            <Hourglass className="size-4" /> Waiting for a parent to check
          </Badge>
        ) : run?.status === "claimed" ? (
          <>
            <Button size="sm" variant="coin" onClick={() => setDoneSheet("submit")}>
              <Check className="size-4" /> I'm done!
            </Button>
            <Button size="sm" variant="ghost" onClick={() => cancel.mutate()} loading={cancel.isPending}>
              <RotateCcw className="size-4" /> Drop it
            </Button>
          </>
        ) : job.locked ? (
          <span className="flex items-center gap-1.5 text-sm font-bold text-locked">
            <Lock className="size-4" /> Finish your responsibilities to unlock
          </span>
        ) : job.available ? (
          <>
            <Button size="sm" onClick={() => claim.mutate(false)} loading={claim.isPending && doneSheet === null}>
              <Play className="size-4" /> Start
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setDoneSheet("claim")}>
              <Check className="size-4" /> Already did it
            </Button>
          </>
        ) : (
          <span className="flex items-center gap-1.5 text-sm font-bold text-muted">
            <Clock className="size-4" /> {job.unavailableReason}
          </span>
        )}
      </div>

      <Sheet
        open={!!doneSheet}
        onClose={() => setDoneSheet(null)}
        title={`Done with “${job.title}”?`}
        footer={
          <Button
            block
            size="lg"
            variant="coin"
            loading={claim.isPending || submit.isPending}
            onClick={() => (doneSheet === "submit" ? submit.mutate() : claim.mutate(true))}
          >
            Send to a parent for approval
          </Button>
        }
      >
        <p className="text-muted">
          A parent will check your work. When they approve it you'll get <Points value={job.reward} family={family} showName />.
        </p>
        <label className="mt-4 block text-sm font-bold">Anything to tell them? (optional)</label>
        <Textarea className="mt-1.5" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="I also wiped the counters!" />
        <p className="mt-1 text-xs text-muted">Tip: no last names, addresses, phone numbers or school names.</p>
      </Sheet>
    </Card>
  );
}
